// admin-panel/src/pages/Login.jsx
import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import toast from 'react-hot-toast';

export default function Login() {
  const { user, login } = useAuth();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success('Welcome back!');
    } catch (err) {
      toast.error(err.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center p-4"
      style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#8B5E3C]/5 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#8B5E3C] to-[#D4AF37] flex items-center justify-center text-2xl font-bold text-white mx-auto mb-4">
            DW
          </div>
          <h1 className="text-xl font-bold text-white">Decentwoods Admin</h1>
          <p className="text-sm text-[#555] mt-1">Sign in to your account</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-[#151515] border border-white/5 rounded-2xl p-6 space-y-4">
          <div>
            <label className="block text-xs text-[#666] mb-1.5">Email Address</label>
            <input type="email" required value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="admin@decentwoods.com"
              className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-4 py-3 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/40 transition-colors" />
          </div>

          <div>
            <label className="block text-xs text-[#666] mb-1.5">Password</label>
            <div className="relative">
              <input type={showPwd ? 'text' : 'password'} required value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                placeholder="Enter password"
                className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-4 py-3 pr-10 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/40 transition-colors" />
              <button type="button" onClick={() => setShowPwd(!showPwd)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#555] hover:text-[#888]">
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button type="submit" disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#8B5E3C] to-[#D4AF37] text-white font-semibold py-3 rounded-xl hover:opacity-90 disabled:opacity-50 transition-all">
            {loading ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <><LogIn size={16} /> Sign In</>
            )}
          </button>
        </form>

        {/* <p className="text-center text-xs text-[#444] mt-4">
          Default: admin@decentwoods.com / password
        </p> */}
      </div>
    </div>
  );
}
